import type { BallState } from '../../types/game';

export interface BallProps {
  ball: BallState;
  context: CanvasRenderingContext2D;
  color?: string;
  strokeColor?: string;
  strokeWidth?: number;
}

/**
 * Ball atomic component - renders a pong ball on canvas
 */
export class Ball {
  private props: BallProps;

  constructor(props: BallProps) {
    this.props = props;
  }

  /**
   * Update ball properties
   */
  public updateProps(props: Partial<BallProps>): void {
    this.props = { ...this.props, ...props };
  }

  /**
   * Render the ball on canvas
   */
  public render(): void {
    const { ball, context, color = '#ffffff', strokeColor = '#ffffff', strokeWidth = 0 } = this.props;
    
    context.save();
    
    // Set ball color
    context.fillStyle = color;
    
    // Draw ball as circle
    context.beginPath();
    context.arc(
      ball.position.x + ball.size / 2,
      ball.position.y + ball.size / 2,
      ball.size / 2,
      0,
      2 * Math.PI
    );
    context.fill();
    
    // Draw stroke if specified
    if (strokeWidth > 0) {
      context.lineWidth = strokeWidth;
      context.strokeStyle = strokeColor;
      context.stroke();
    }
    
    context.restore();
  }

  /**
   * Get ball bounds for collision detection
   */
  public getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.props.ball.position.x,
      y: this.props.ball.position.y,
      width: this.props.ball.size,
      height: this.props.ball.size
    };
  }

  /**
   * Check if point is inside ball
   */
  public containsPoint(x: number, y: number): boolean {
    const centerX = this.props.ball.position.x + this.props.ball.size / 2;
    const centerY = this.props.ball.position.y + this.props.ball.size / 2;
    const radius = this.props.ball.size / 2;
    
    const distance = Math.sqrt(
      Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
    );
    
    return distance <= radius;
  }
}

/**
 * Factory function to create Ball instances
 */
export function createBall(props: BallProps): Ball {
  return new Ball(props);
}

/**
 * Static rendering function for functional-style usage
 */
export function renderBall(props: BallProps): void {
  const ball = new Ball(props);
  ball.render();
} 